import { useCallback, useMemo, useState } from 'react'
import { useProjectDetail } from '../hooks/useProjectDetail'
import type { ProjectDetailResponse, ProjectDetailProject } from '../types'
import { authFetchJson } from '../lib/authFetch'
import { getVisibleTabs, type TabStatus } from '../lib/tabRegistry'
import { SECTION_REGISTRY } from '../lib/sectionRegistry'
import { useSectionCompletions, type SectionCompletionState } from '../hooks/useSectionCompletions'

// Tab components (all 21 tabs)
import {
  QuickEntryTab,
  DocumentsTab,
  OverviewTab,
  ProjectTeamTab,
  IPTransferTab,
  TechnologyTab,
  DevPhaseTab,
  CompetitionTab,
  FundingTab,
  PublicationsTab,
  OutreachTab,
  VBTeamTab,
  TermSheetTab,
  VentureBuildingTab,
  FundraisingTab,
  FundedTab,
} from './projects/tabs'

// Special tabs (voting, defensibility, IRL, scoring, recommendation)
import { IDAReviewPanel } from './voting/IDAReviewPanel'
import { VBRecommendationPanel } from './voting/VBRecommendationPanel'
import { DefensibilityTab } from './assessments/DefensibilityTab'
import { IRLAssessmentTab } from './assessments/IRLAssessmentTab'
import { ScoringTab } from './project-cards/shared/ScoringTab'

// Header components
import { ProjectHeader } from './projects/ProjectHeader'
// PipelineBar removed — stage visible in header badge
import { ReviewTeamBar } from './projects/ReviewTeamBar'
import { StageTabNav } from './projects/StageTabNav'
import { ProjectActionBar } from './projects/ProjectActionBar'

interface ProjectDetailProps {
  projectId: string | null
  onClose: () => void
  onUpdate?: (updated: ProjectDetailResponse) => void
}

// Tab content props interface (matches QuickEntryTab interface)
interface TabContentProps {
  project: ProjectDetailProject
  projectId: number
  domain: string
  onFieldChange: (field: string, value: any) => void
  data?: ProjectDetailResponse
  sectionCompletions?: SectionCompletionState
  tabId?: string
}

/**
 * Compute tab statuses from section completions (replaces heuristic getTabStatuses)
 */
function getTabStatusesFromCompletions(
  sc: SectionCompletionState,
  visibleTabs: { id: string }[],
): Record<string, TabStatus> {
  const result: Record<string, TabStatus> = {}
  for (const tab of visibleTabs) {
    const sections = SECTION_REGISTRY[tab.id] || []
    if (sections.length === 0) {
      result[tab.id] = 'none' // e.g. Documents — no completable sections
      continue
    }
    const { completed, total } = sc.getTabCompletedCount(tab.id, sections.length)
    if (completed === 0) {
      result[tab.id] = 'empty'
    } else if (completed >= total) {
      result[tab.id] = 'complete'
    } else {
      result[tab.id] = 'partial'
    }
  }
  return result
}

// Map tab IDs to components
const TAB_COMPONENTS: Record<string, React.ComponentType<any>> = {
  'tab-quick-entry': QuickEntryTab,
  'tab-documents': DocumentsTab,
  'tab-overview': OverviewTab,
  'tab-team': ProjectTeamTab,
  'tab-ip': IPTransferTab,
  'tab-technology': TechnologyTab,
  'tab-devphase': DevPhaseTab,
  'tab-competition': CompetitionTab,
  'tab-funding': FundingTab,
  'tab-publications': PublicationsTab,
  'tab-defensibility': DefensibilityTab,
  'tab-voting': IDAReviewPanel,
  'tab-outreach': OutreachTab,
  'tab-irl': IRLAssessmentTab,
  'tab-scoring': ScoringTab,
  'tab-vb-recommendation': VBRecommendationPanel,
  'tab-vbteam': VBTeamTab,
  'tab-termsheet': TermSheetTab,
  'tab-venturebuilding': VentureBuildingTab,
  'tab-fundraising': FundraisingTab,
  'tab-funded': FundedTab,
}

/**
 * Determine initial tab based on pipeline stage
 */
function getInitialTab(stage: string): string {
  const visibleTabs = getVisibleTabs(stage)
  if (visibleTabs.length === 0) return 'tab-quick-entry'

  // Stage-specific defaults
  switch (stage) {
    case 'INBOX':
      return 'tab-quick-entry'
    case 'LONG_LIST':
      return 'tab-overview'
    case 'IN_REVIEW':
    case 'EXTRA_MILE':
    case 'VB_PROCESS':
    case 'FUNDED':
      // Return first IN_REVIEW tab or visible tab
      return visibleTabs.find(t => t.stage === 'IN_REVIEW')?.id || visibleTabs[0]?.id || 'tab-quick-entry'
    case 'PARKED':
    case 'GRAVEYARD':
      return 'tab-quick-entry'
    default:
      return visibleTabs[0]?.id || 'tab-quick-entry'
  }
}

export function ProjectDetail({ projectId, onClose, onUpdate }: ProjectDetailProps) {
  const { data, loading, error, mutateProject, refetch } = useProjectDetail(projectId)
  const [activeTab, setActiveTab] = useState<string>('')
  const numericIdForHook = projectId ? parseInt(projectId, 10) : 0
  const sc = useSectionCompletions(numericIdForHook)

  // Initialize activeTab when data loads
  if (data && !activeTab) {
    const initialTab = getInitialTab(data.project.pipeline_stage)
    setActiveTab(initialTab)
  }

  const numericId = projectId ? parseInt(projectId, 10) : null

  // Handle field changes via PATCH API
  const handleFieldChange = useCallback(
    async (field: string, value: any) => {
      if (!projectId) return
      // Snapshot previous value so we can revert on failure
      const prevSnapshot = (data?.project as any)?.[field]
      try {
        // Optimistically update local state so UI reflects change immediately
        mutateProject({ [field]: value } as any)

        const updated = await authFetchJson<{ project: any }>(
          `/api/v2/projects/${projectId}`,
          {
            method: 'PATCH',
            json: { [field]: value },
          }
        )
        // Merge server response into local state (server is authoritative)
        if (updated?.project) {
          mutateProject(updated.project)
        }
        if (onUpdate && updated) {
          onUpdate(updated as any)
        }
      } catch (err) {
        // Revert optimistic update so UI doesn't lie about persisted state
        mutateProject({ [field]: prevSnapshot } as any)
        const msg =
          err instanceof Error && 'response' in err
            ? ((err as any).response?.error as string | undefined) || err.message
            : err instanceof Error
              ? err.message
              : String(err)
        console.error('Field update failed:', { field, value, err })
        // Surface as a browser alert for now — TODO: replace with toast component.
        // Better UX than the previous silent swallow that made fields appear to
        // revert "for no reason" (S9 hotfix round 3).
        alert(`Could not save ${field}: ${msg}`)
      }
    },
    [projectId, onUpdate, mutateProject, data]
  )

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col h-full bg-bg-surface">
        <div className="flex-1 p-6">
          <div className="space-y-4 animate-pulse">
            {[1, 2, 3].map(i => (
              <div key={i}>
                <div className="h-3 bg-bg-surface-hover rounded w-24 mb-2" />
                <div className="h-4 bg-bg-surface-hover rounded w-3/4" />
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col h-full bg-bg-surface p-6">
        <div className="flex items-center gap-2 text-primary-red text-body p-4 bg-primary-red/10 rounded-md">
          <span>⚠</span> {error}
        </div>
        <button
          onClick={onClose}
          className="mt-4 text-sm text-text-secondary hover:text-text-primary"
        >
          ← Back
        </button>
      </div>
    )
  }

  if (!data || !numericId) return null

  const { project } = data
  const visibleTabs = getVisibleTabs(project.pipeline_stage)
  const currentTab = activeTab || getInitialTab(project.pipeline_stage)

  // Limited view for PARKED/GRAVEYARD
  const isLimitedView = ['PARKED', 'GRAVEYARD'].includes(project.pipeline_stage)

  return (
    <div className="flex flex-col h-full bg-bg-surface">
      {/* Project Header */}
      <ProjectHeader project={project} onClose={onClose} />

      {/* Project Action Bar — visible across all tabs (S9-hotfix6) */}
      {!isLimitedView && (
        <div className="px-6 pt-3 flex justify-end">
          <ProjectActionBar
            projectId={numericId}
            currentStage={project.pipeline_stage}
            onTransitioned={refetch}
          />
        </div>
      )}

      {/* Review Team Bar (conditionally shown) */}
      {!isLimitedView && (
        <div className="px-6 pt-5 pb-2">
          <ReviewTeamBar
            projectId={numericId}
            currentStage={project.pipeline_stage}
            fallbackOwner={
              project.owner
                ? { name: project.owner.name, avatar_url: project.owner.picture_url }
                : undefined
            }
          />
        </div>
      )}

      {/* Tab Navigation */}
      {!isLimitedView && (
        <div className="mt-3" />
      )}
      {!isLimitedView && data && (
        <StageTabNav
          currentStage={project.pipeline_stage}
          activeTabId={currentTab}
          onTabChange={setActiveTab}
          tabStatuses={getTabStatusesFromCompletions(sc, visibleTabs)}
        />
      )}

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto px-6 py-6">
          {isLimitedView ? (
            // Limited view: only Quick Entry and Documents
            <>
              {(currentTab === 'tab-quick-entry' || !currentTab) && (
                <QuickEntryTab
                  project={project}
                  projectId={numericId}
                  domain={project.domain || 'biotech'}
                  onFieldChange={handleFieldChange}
                  initialResearchers={data.researchers}
                />
              )}
              {currentTab === 'tab-documents' && (
                <DocumentsTab
                  project={project}
                  projectId={numericId}
                  domain={project.domain || 'biotech'}
                  onFieldChange={handleFieldChange}
                  initialFiles={data.files}
                />
              )}
            </>
          ) : (
            // Full lifecycle view
            <>
              {currentTab === 'tab-quick-entry' && (
                <QuickEntryTab
                  project={project}
                  projectId={numericId}
                  domain={project.domain || 'biotech'}
                  onFieldChange={handleFieldChange}
                  sectionCompletions={sc}
                  tabId="tab-quick-entry"
                  initialResearchers={data.researchers}
                />
              )}
              {currentTab === 'tab-documents' && (
                <DocumentsTab
                  project={project}
                  projectId={numericId}
                  domain={project.domain || 'biotech'}
                  onFieldChange={handleFieldChange}
                  initialFiles={data.files}
                />
              )}
              {currentTab === 'tab-overview' && (
                <OverviewTab
                  project={project}
                  projectId={numericId}
                  domain={project.domain || 'biotech'}
                  onFieldChange={handleFieldChange}
                  sectionCompletions={sc}
                  tabId="tab-overview"
                />
              )}
              {currentTab === 'tab-team' && (
                <ProjectTeamTab
                  project={project}
                  projectId={numericId}
                  domain={project.domain || 'biotech'}
                  onFieldChange={handleFieldChange}
                  sectionCompletions={sc}
                  tabId="tab-team"
                />
              )}
              {currentTab === 'tab-ip' && (
                <IPTransferTab
                  project={project}
                  projectId={numericId}
                  domain={project.domain || 'biotech'}
                  onFieldChange={handleFieldChange}
                  sectionCompletions={sc}
                  tabId="tab-ip"
                />
              )}
              {currentTab === 'tab-technology' && (
                <TechnologyTab
                  project={project}
                  projectId={numericId}
                  domain={project.domain || 'biotech'}
                  onFieldChange={handleFieldChange}
                  sectionCompletions={sc}
                  tabId="tab-technology"
                />
              )}
              {currentTab === 'tab-devphase' && (
                <DevPhaseTab
                  project={project}
                  projectId={numericId}
                  domain={project.domain || 'biotech'}
                  onFieldChange={handleFieldChange}
                  sectionCompletions={sc}
                  tabId="tab-devphase"
                />
              )}
              {currentTab === 'tab-competition' && (
                <CompetitionTab
                  project={project}
                  projectId={numericId}
                  domain={project.domain || 'biotech'}
                  onFieldChange={handleFieldChange}
                  sectionCompletions={sc}
                  tabId="tab-competition"
                />
              )}
              {currentTab === 'tab-funding' && (
                <FundingTab
                  project={project}
                  projectId={numericId}
                  domain={project.domain || 'biotech'}
                  onFieldChange={handleFieldChange}
                  sectionCompletions={sc}
                  tabId="tab-funding"
                />
              )}
              {currentTab === 'tab-publications' && (
                <PublicationsTab
                  project={project}
                  projectId={numericId}
                  domain={project.domain || 'biotech'}
                  onFieldChange={handleFieldChange}
                  sectionCompletions={sc}
                  tabId="tab-publications"
                />
              )}
              {currentTab === 'tab-defensibility' && (
                <DefensibilityTab projectId={numericId} />
              )}
              {currentTab === 'tab-voting' && (
                <IDAReviewPanel projectId={numericId} />
              )}
              {currentTab === 'tab-outreach' && (
                <OutreachTab projectId={numericId} />
              )}
              {currentTab === 'tab-irl' && (
                <IRLAssessmentTab projectId={numericId} />
              )}
              {currentTab === 'tab-scoring' && (
                <ScoringTab data={data} />
              )}
              {currentTab === 'tab-vb-recommendation' && (
                <VBRecommendationPanel projectId={numericId} />
              )}
              {currentTab === 'tab-vbteam' && (
                <VBTeamTab projectId={numericId} />
              )}
              {currentTab === 'tab-termsheet' && (
                <TermSheetTab projectId={numericId} />
              )}
              {currentTab === 'tab-venturebuilding' && (
                <VentureBuildingTab projectId={numericId} />
              )}
              {currentTab === 'tab-fundraising' && (
                <FundraisingTab projectId={numericId} />
              )}
              {currentTab === 'tab-funded' && (
                <FundedTab projectId={numericId} />
              )}
            </>
          )}
      </div>
    </div>
  )
}
